import { groupGistsByParent } from "../../../services/utils/groupGistsByParent";
import { linkifyPlainTextUrlsInHtml } from "../../../utils/richHtmlSanitizer";
import type { GistType } from "../../../services/types";
import { INITIAL_DISCUSSION_PREVIEW_COUNT } from "./constants";
import type {
  DiscussionPreviewItem,
  FeaturedSlot,
  GistComment,
  GistEdit,
  GistItem,
  GistReaderMode,
  GistSiblingNavigation,
  GistTreeNode,
  GistVersion,
  SubjectItem,
  SubjectStats,
  SubjectTreeView,
  VisibleSubjectRow,
} from "./types";

export function buildSubjectMap(subjects: SubjectItem[]): Record<number, SubjectItem> {
  return subjects.reduce<Record<number, SubjectItem>>((acc, subject) => {
    acc[subject.id] = subject;
    return acc;
  }, {});
}

export function sortByTitle<T extends { title: string }>(items: T[]): T[] {
  return items.slice().sort((a, b) => a.title.localeCompare(b.title));
}

export function buildBuckets(subjects: SubjectItem[]): Record<string, SubjectItem[]> {
  const byId = buildSubjectMap(subjects);
  const buckets: Record<string, SubjectItem[]> = {};

  subjects.forEach((subject) => {
    const parentExists =
      typeof subject.parentId === "number" && Boolean(byId[subject.parentId]);
    const key = parentExists ? String(subject.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(subject);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = sortByTitle(buckets[key]);
  });

  return buckets;
}

export function getDescendantIds(
  subjectId: number,
  buckets: Record<string, SubjectItem[]>,
): number[] {
  const result: number[] = [];
  const visit = (id: number) => {
    (buckets[String(id)] || []).forEach((child) => {
      result.push(child.id);
      visit(child.id);
    });
  };
  visit(subjectId);
  return result;
}

export function getSubjectPath(
  subjects: SubjectItem[],
  subjectId: number | null,
): SubjectItem[] {
  if (subjectId === null) return [];
  const byId = buildSubjectMap(subjects);
  const path: SubjectItem[] = [];
  const visited = new Set<number>();
  let current: SubjectItem | undefined = byId[subjectId];

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current =
      typeof current.parentId === "number" ? byId[current.parentId] : undefined;
  }

  return path;
}

export function stripHtml(value: string): string {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function makeExcerpt(value: string, maxLength = 145): string {
  const text = stripHtml(value);
  if (!text) return "Open this gist to read the current text and discussion.";
  return text.length > maxLength
    ? `${text.slice(0, maxLength).trim()}...`
    : text;
}

export function makeReaderParagraphs(value: string, maxParagraphs?: number): string[] {
  const text = stripHtml(value);
  if (!text)
    return [
      "This gist does not have readable body text yet. Open the discussion tools below to continue exploring it.",
    ];

  const roughSentences = text
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (roughSentences.length <= 3) {
    return [text];
  }

  const paragraphs: string[] = [];
  for (let index = 0; index < roughSentences.length; index += 2) {
    paragraphs.push(roughSentences.slice(index, index + 2).join(" "));
    if (typeof maxParagraphs === "number" && paragraphs.length >= maxParagraphs)
      break;
  }

  return paragraphs;
}

export function formatDate(value?: string | null): string {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function compactNumber(value: number): string {
  if (value >= 1000) {
    const rounded = value / 1000;
    return `${rounded.toFixed(rounded >= 10 ? 0 : 1)}K`;
  }
  return String(value);
}


type CanonicalGistOrderKey = number[];

function parseLeadingNumericOrder(value: string | null | undefined): CanonicalGistOrderKey | null {
  const title = String(value ?? "").trim();
  const match = title.match(/^(\d+(?:\.\d+)*)(?=\s*(?:[·:.)\-—–]|$))/);

  if (!match) return null;

  const parts = match[1]
    .split(".")
    .map((part) => Number.parseInt(part, 10));

  if (!parts.length || parts.some((part) => !Number.isFinite(part))) return null;
  return parts;
}

function compareCanonicalOrderKeys(
  a: CanonicalGistOrderKey,
  b: CanonicalGistOrderKey,
): number {
  const maxLength = Math.max(a.length, b.length);

  for (let index = 0; index < maxLength; index += 1) {
    const aPart = a[index] ?? -1;
    const bPart = b[index] ?? -1;
    if (aPart !== bPart) return aPart - bPart;
  }

  return 0;
}

export function getGistCanonicalOrderKey(gist: GistItem): CanonicalGistOrderKey | null {
  return parseLeadingNumericOrder(gist.title);
}

export function compareGistsCanonically(
  a: GistItem,
  b: GistItem,
  fallbackCompare: (a: GistItem, b: GistItem) => number,
): number {
  const aKey = getGistCanonicalOrderKey(a);
  const bKey = getGistCanonicalOrderKey(b);

  if (aKey && bKey) {
    const canonicalDiff = compareCanonicalOrderKeys(aKey, bKey);
    if (canonicalDiff !== 0) return canonicalDiff;
    return a.title.localeCompare(b.title);
  }

  return fallbackCompare(a, b);
}

export function sortGistsCanonically(
  gists: GistItem[],
  fallbackCompare: (a: GistItem, b: GistItem) => number,
): GistItem[] {
  return gists.slice().sort((a, b) => compareGistsCanonically(a, b, fallbackCompare));
}

export function countGistMetrics(gist: GistItem) {
  const versions = gist.versions || [];
  const edits = versions.flatMap((version) => version.edits || []);
  const comments = edits
    .flatMap((edit) => edit.comments || [])
    .filter((comment) => !comment.isRemoved);
  const latestCandidates = [
    gist.createdAt,
    gist.publishedAt,
    ...versions.map((version) => version.createdAt),
    ...edits.map((edit) => edit.createdAt),
  ].filter(Boolean) as string[];

  const latestActivity =
    latestCandidates
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0]
      ?.toISOString() || null;

  return {
    commentCount: comments.length,
    editCount: edits.length,
    keyPointCount: versions.filter((version) => Boolean(version.point?.trim()))
      .length,
    latestActivity,
  };
}

export function getFirstMeaningfulText(gist: GistItem): string {
  const point = getLatestVersion(gist)?.point;
  if (point?.trim()) return point;

  const body = getLatestEditBody(gist);
  return body || "Open this gist to read the current text and discussion.";
}

export function getFullReaderText(gist: GistItem): string {
  return getLatestEditBody(gist) || getFirstMeaningfulText(gist);
}

export function canUserEditGist(gist: GistItem, user: any): boolean {
  if (!user) return false;
  if (user.userType === "admin" || user.userType === "moderator") return true;
  if (gist.canEditInPlace) return true;
  const latestEdit = getLatestEdit(gist);
  if (latestEdit?.user?.id && latestEdit.user.id === user.id) return true;
  return false;
}

export function normalizeRenderedHtml(html: string | undefined | null): string {
  if (!html) return "";
  const normalizedHtml = html
    .replace(/(<p>\s*(<br\s*\/?>)?\s*<\/p>\s*)+/gi, "")
    .replace(/(<br\s*\/?>\s*){3,}/gi, "<br/><br/>");

  return linkifyPlainTextUrlsInHtml(normalizedHtml);
}

export function getAuthorName(gist: GistItem): string {
  return (
    gist.versions?.find((version) => version.user?.name)?.user?.name ||
    "Gistoscope Team"
  );
}

export function normalizeReaderMode(value: string | null): GistReaderMode {
  if (value === "edit" || value === "tools") return "tools";
  if (value === "advanced") return value;
  return "read";
}

export function getLatestVersion(gist: GistItem): GistVersion | null {
  const versions = gist.versions || [];
  if (!versions.length) return null;
  return versions
    .slice()
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
}

export function getLatestEdit(gist: GistItem): GistEdit | null {
  const edits = (gist.versions || []).flatMap((version) => version.edits || []);
  if (!edits.length) return null;
  return edits
    .slice()
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
}

export function getLatestEditBody(gist: GistItem): string {
  return getLatestEdit(gist)?.body || "";
}

export function getAuthorDetails(gist: GistItem) {
  const latestEdit = getLatestEdit(gist);
  const latestVersion = getLatestVersion(gist);
  return {
    name:
      latestEdit?.user?.name || latestVersion?.user?.name || "Gistoscope Team",
    image:
      latestEdit?.user?.image || latestVersion?.user?.image || "/profile.png",
    date:
      latestEdit?.createdAt ||
      latestVersion?.createdAt ||
      countGistMetrics(gist).latestActivity ||
      gist.createdAt,
  };
}

export function getSummaryText(gist: GistItem): string {
  const latestPoint = getLatestVersion(gist)?.point;
  if (latestPoint?.trim()) return stripHtml(latestPoint);
  return makeExcerpt(getFullReaderText(gist), 180);
}

export function getVoteMetrics(gist: GistItem) {
  const edits = (gist.versions || []).flatMap((version) => version.edits || []);
  const newness = edits.reduce(
    (total, edit) => total + (edit.newnessCount || 0),
    0,
  );
  const important = edits.reduce(
    (total, edit) => total + (edit.importantCount || 0),
    0,
  );
  const quality = edits.reduce(
    (total, edit) => total + (edit.qualityCount || 0),
    0,
  );
  return {
    newness,
    important,
    quality,
    total: newness + important + quality,
  };
}

export function getGistComments(gist: GistItem): GistComment[] {
  const seen = new Set<number>();
  return (gist.versions || [])
    .flatMap((version) => version.edits || [])
    .flatMap((edit) => edit.comments || [])
    .filter((comment) => {
      if (!comment || comment.isRemoved || !comment.comment?.trim())
        return false;
      if (seen.has(comment.id)) return false;
      seen.add(comment.id);
      return true;
    });
}

export function getCommentText(comment: GistComment): string {
  return stripHtml(comment.comment || "");
}

export function getCommentAuthor(comment: GistComment): string {
  return comment.user?.name || "Gistoscope user";
}

export function getCommentInitials(comment: GistComment): string {
  const name = getCommentAuthor(comment).trim();
  if (!name) return "G";
  const parts = name.split(/\s+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "G"
  );
}

export function getCommentDepth(
  comment: GistComment,
  byId: Map<number, GistComment>,
): number {
  let depth = 0;
  const visited = new Set<number>();
  let current =
    typeof comment.parentId === "number"
      ? byId.get(comment.parentId)
      : undefined;

  while (current && !visited.has(current.id) && depth < 4) {
    visited.add(current.id);
    depth += 1;
    current =
      typeof current.parentId === "number"
        ? byId.get(current.parentId)
        : undefined;
  }

  return depth;
}

export function getCommentUsefulScore(
  comment: GistComment,
  childCount: number,
): number {
  const text = getCommentText(comment);
  const substanceScore = Math.min(text.length / 140, 6);
  const replyScore = Math.min(childCount, 6) * 2;
  const createdAtMs = comment.createdAt
    ? new Date(comment.createdAt).getTime()
    : NaN;
  const recencyScore = Number.isFinite(createdAtMs)
    ? Math.max(0, 2 - (Date.now() - createdAtMs) / 1000 / 60 / 60 / 24 / 30)
    : 0;
  return substanceScore + replyScore + recencyScore;
}

export function buildUsefulDiscussionPreview(
  comments: GistComment[],
): DiscussionPreviewItem[] {
  if (!comments.length) return [];

  const byId = new Map<number, GistComment>();
  comments.forEach((comment) => byId.set(comment.id, comment));

  const childrenByParent = new Map<number, GistComment[]>();
  comments.forEach((comment) => {
    if (typeof comment.parentId !== "number" || !byId.has(comment.parentId))
      return;
    const children = childrenByParent.get(comment.parentId) || [];
    children.push(comment);
    childrenByParent.set(comment.parentId, children);
  });

  const sortUseful = (items: GistComment[]) =>
    items.slice().sort((a, b) => {
      const scoreDiff =
        getCommentUsefulScore(b, childrenByParent.get(b.id)?.length || 0) -
        getCommentUsefulScore(a, childrenByParent.get(a.id)?.length || 0);
      if (scoreDiff !== 0) return scoreDiff;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });

  const roots = comments.filter(
    (comment) =>
      typeof comment.parentId !== "number" || !byId.has(comment.parentId),
  );
  const highlightedPath: GistComment[] = [];
  const highlightedIds = new Set<number>();
  let cursor = sortUseful(roots)[0];

  while (
    cursor &&
    !highlightedIds.has(cursor.id) &&
    highlightedPath.length < INITIAL_DISCUSSION_PREVIEW_COUNT
  ) {
    highlightedPath.push(cursor);
    highlightedIds.add(cursor.id);
    cursor = sortUseful(childrenByParent.get(cursor.id) || [])[0];
  }

  const rest = sortUseful(
    comments.filter((comment) => !highlightedIds.has(comment.id)),
  );
  return [...highlightedPath, ...rest].map((comment) => ({
    comment,
    depth: Math.min(getCommentDepth(comment, byId), 3),
    isHighlightedPath: highlightedIds.has(comment.id),
  }));
}

export function childrenCountLabel(
  comment: GistComment,
  comments: GistComment[],
): string {
  const count = comments.filter(
    (candidate) => candidate.parentId === comment.id,
  ).length;
  if (!count) return "";
  return `${count} ${count === 1 ? "reply" : "replies"}`;
}

export function getReplyGists(gist: GistItem, allGists: GistItem[]): GistItem[] {
  return allGists.filter((candidate) => candidate.parentId === gist.id);
}

export function getParentGist(gist: GistItem, allGists: GistItem[]): GistItem | null {
  if (typeof gist.parentId !== "number") return null;
  return allGists.find((candidate) => candidate.id === gist.parentId) || null;
}

export function sortContextGists(gists: GistItem[]): GistItem[] {
  return gists.slice().sort((a, b) => {
    const aActivity = countGistMetrics(a).latestActivity || a.createdAt || "";
    const bActivity = countGistMetrics(b).latestActivity || b.createdAt || "";
    return bActivity.localeCompare(aActivity) || a.title.localeCompare(b.title);
  });
}

export function sortGistTreeSiblings(
  siblingGists: GistItem[],
  allGists: GistItem[],
): GistItem[] {
  const fallbackCompare = (a: GistItem, b: GistItem) => {
    const aActivity = countGistMetrics(a).latestActivity || a.createdAt || "";
    const bActivity = countGistMetrics(b).latestActivity || b.createdAt || "";
    const aChildren = allGists.filter((candidate) => candidate.parentId === a.id)
      .length;
    const bChildren = allGists.filter((candidate) => candidate.parentId === b.id)
      .length;
    if (aChildren !== bChildren) return bChildren - aChildren;
    return bActivity.localeCompare(aActivity) || a.title.localeCompare(b.title);
  };

  return sortGistsCanonically(siblingGists, fallbackCompare);
}

export function getGistSiblingNavigation(
  gist: GistItem,
  allGists: GistItem[],
): GistSiblingNavigation {
  const hasParent = typeof gist.parentId === "number";
  const siblings = sortGistTreeSiblings(
    allGists.filter((candidate) => {
      if (hasParent) return candidate.parentId === gist.parentId;
      const candidateIsRoot = typeof candidate.parentId !== "number";
      return candidateIsRoot && candidate.subjectId === gist.subjectId;
    }),
    allGists,
  );
  const currentSiblingIndex = siblings.findIndex(
    (candidate) => candidate.id === gist.id,
  );

  return {
    currentSiblingIndex,
    previousSibling:
      currentSiblingIndex > 0 ? siblings[currentSiblingIndex - 1] : null,
    nextSibling:
      currentSiblingIndex >= 0 && currentSiblingIndex < siblings.length - 1
        ? siblings[currentSiblingIndex + 1]
        : null,
  };
}

export function getAncestorGists(gist: GistItem, allGists: GistItem[]): GistItem[] {
  const byId = allGists.reduce<Record<number, GistItem>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  const ancestors: GistItem[] = [];
  const visited = new Set<number>();
  let current =
    typeof gist.parentId === "number" ? byId[gist.parentId] : undefined;

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    ancestors.unshift(current);
    current =
      typeof current.parentId === "number" ? byId[current.parentId] : undefined;
  }

  return ancestors;
}

export function buildStats(
  subjects: SubjectItem[],
  gists: GistItem[],
  buckets: Record<string, SubjectItem[]>,
): Record<number, SubjectStats> {
  const stats: Record<number, SubjectStats> = {};

  subjects.forEach((subject) => {
    const descendantIds = getDescendantIds(subject.id, buckets);
    const branchSubjectIds = new Set([subject.id, ...descendantIds]);
    const branchGists = gists.filter(
      (gist) =>
        typeof gist.subjectId === "number" &&
        branchSubjectIds.has(gist.subjectId),
    );
    const directGists = gists.filter((gist) => gist.subjectId === subject.id);
    const gistMetrics = branchGists.map(countGistMetrics);
    const latestActivity =
      gistMetrics
        .map((metric) => metric.latestActivity)
        .filter(Boolean)
        .sort()
        .reverse()[0] || null;

    stats[subject.id] = {
      directGistCount: directGists.length,
      branchGistCount: branchGists.length,
      childCount: (buckets[String(subject.id)] || []).length,
      descendantCount: descendantIds.length,
      commentCount: gistMetrics.reduce(
        (total, metric) => total + metric.commentCount,
        0,
      ),
      editCount: gistMetrics.reduce(
        (total, metric) => total + metric.editCount,
        0,
      ),
      keyPointCount: gistMetrics.reduce(
        (total, metric) => total + metric.keyPointCount,
        0,
      ),
      latestActivity,
    };
  });

  return stats;
}

export function buildSubjectDescription(
  subject: SubjectItem,
  stat: SubjectStats | undefined,
): string {
  const title = subject.title.trim();
  const gistCount = stat?.branchGistCount || 0;
  const childCount = stat?.childCount || 0;

  if (title.toLowerCase() === "news") {
    return "This subject covers current events, media, and the evolving stories shaping our world.";
  }
  if (gistCount > 0 && childCount > 0) {
    return `This subject gathers ${gistCount} connected gists and ${childCount} subtopics around ${title}.`;
  }
  if (gistCount > 0) {
    return `This subject gathers ${gistCount} connected gists around ${title}.`;
  }
  if (childCount > 0) {
    return `This subject contains ${childCount} subtopics that can be explored in more detail.`;
  }
  return `This subject is a clean starting point for future gists about ${title}.`;
}

export function chooseFeaturedSlots(branchGists: GistItem[]): FeaturedSlot[] {
  const usedIds = new Set<number>();
  const byActivity = branchGists
    .slice()
    .sort((a, b) =>
      (countGistMetrics(b).latestActivity || b.createdAt || "").localeCompare(
        countGistMetrics(a).latestActivity || a.createdAt || "",
      ),
    );
  const byComments = branchGists
    .slice()
    .sort(
      (a, b) =>
        countGistMetrics(b).commentCount - countGistMetrics(a).commentCount,
    );
  const rootGists = branchGists.filter((gist) => !gist.parentId);

  const take = (candidates: GistItem[]) => {
    const found = candidates.find((gist) => !usedIds.has(gist.id)) || null;
    if (found) usedIds.add(found.id);
    return found;
  };

  const overview = take(rootGists.length ? rootGists : byActivity);
  const debated = take(byComments);
  const recent = take(byActivity);

  return [
    {
      key: "overview",
      label: "Best starting point",
      title: "Main overview",
      icon: "book",
      gist: overview,
    },
    {
      key: "debated",
      label: "Highest discussion activity",
      title: "Most debated",
      icon: "flame",
      gist: debated,
    },
    {
      key: "recent",
      label: "Active recently",
      title: "Recent important thread",
      icon: "clock",
      gist: recent,
    },
  ];
}

export function getSubjectUrl(subject: SubjectItem): string {
  return `/subject/${encodeURIComponent(subject.title)}/${subject.id}`;
}

export function getAllExpandableSubjectIds(
  subjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
): Set<number> {
  return new Set(
    subjects
      .filter((subject) => (buckets[String(subject.id)] || []).length > 0)
      .map((subject) => subject.id),
  );
}


export function getSubjectDescendantIds(
  subjectId: number,
  buckets: Record<string, SubjectItem[]>,
): Set<number> {
  const descendantIds = new Set<number>();

  const visit = (id: number) => {
    (buckets[String(id)] || []).forEach((child) => {
      if (descendantIds.has(child.id)) return;
      descendantIds.add(child.id);
      visit(child.id);
    });
  };

  visit(subjectId);
  return descendantIds;
}

export function getSubjectAncestorIds(
  subjectId: number,
  subjects: SubjectItem[],
): number[] {
  const byId = new Map(subjects.map((subject) => [subject.id, subject]));
  const ancestorIds: number[] = [];
  const visitedIds = new Set<number>();
  let current = byId.get(subjectId);

  while (current && typeof current.parentId === "number") {
    if (visitedIds.has(current.parentId)) break;
    visitedIds.add(current.parentId);
    const parent = byId.get(current.parentId);
    if (!parent) break;
    ancestorIds.unshift(parent.id);
    current = parent;
  }

  return ancestorIds;
}

export function getInitialFocusedSubjectExpandedIds(
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
): Set<number> {
  return new Set(
    rootSubjects
      .filter((subject) => (buckets[String(subject.id)] || []).length > 0)
      .map((subject) => subject.id),
  );
}

export function getFocusedSubjectExpandedIds(
  subjectId: number,
  subjects: SubjectItem[],
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
): Set<number> {
  const next = getInitialFocusedSubjectExpandedIds(rootSubjects, buckets);

  getSubjectAncestorIds(subjectId, subjects).forEach((ancestorId) => {
    if ((buckets[String(ancestorId)] || []).length > 0) {
      next.add(ancestorId);
    }
  });

  if ((buckets[String(subjectId)] || []).length > 0) {
    next.add(subjectId);
  }

  return next;
}

export function getNextFocusedSubjectExpandedIds(
  subjectId: number,
  subjects: SubjectItem[],
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
  currentExpandedIds: Set<number>,
): Set<number> {
  const subject = subjects.find((item) => item.id === subjectId) || null;
  if (!subject) return currentExpandedIds;

  const next = new Set(currentExpandedIds);
  const descendants = getSubjectDescendantIds(subjectId, buckets);
  const isExpanded = next.has(subjectId);

  if (isExpanded) {
    next.delete(subjectId);
    descendants.forEach((descendantId) => next.delete(descendantId));
    return next;
  }

  const siblingSubjects =
    typeof subject.parentId === "number"
      ? subjects.filter((item) => item.parentId === subject.parentId)
      : rootSubjects;

  siblingSubjects.forEach((sibling) => {
    if (sibling.id === subjectId) return;
    next.delete(sibling.id);
    getSubjectDescendantIds(sibling.id, buckets).forEach((descendantId) =>
      next.delete(descendantId),
    );
  });

  getSubjectAncestorIds(subjectId, subjects).forEach((ancestorId) => {
    if ((buckets[String(ancestorId)] || []).length > 0) {
      next.add(ancestorId);
    }
  });

  if ((buckets[String(subjectId)] || []).length > 0) {
    next.add(subjectId);
  }

  return next;
}

export function getVisibleSubjectRows(
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
  expandedIds: Set<number>,
  pickerMode: "tree" | "list",
): VisibleSubjectRow[] {
  if (pickerMode === "list") {
    return rootSubjects.map((subject) => ({ subject, depth: 0 }));
  }

  const rows: VisibleSubjectRow[] = [];

  const visit = (subject: SubjectItem, depth: number) => {
    rows.push({ subject, depth });
    if (!expandedIds.has(subject.id)) return;
    (buckets[String(subject.id)] || []).forEach((child) =>
      visit(child, depth + 1),
    );
  };

  rootSubjects.forEach((subject) => visit(subject, 0));
  return rows;
}

export function normalizeSubjectSearch(value: string): string {
  return value.trim().toLowerCase();
}

export function subjectMatchesSearch(
  subject: SubjectItem,
  normalizedQuery: string,
): boolean {
  return (
    normalizedQuery.length > 0 &&
    subject.title.toLowerCase().includes(normalizedQuery)
  );
}

export function subjectHasMatchingDescendant(
  subject: SubjectItem,
  buckets: Record<string, SubjectItem[]>,
  normalizedQuery: string,
): boolean {
  if (!normalizedQuery) return false;
  return (buckets[String(subject.id)] || []).some(
    (child) =>
      subjectMatchesSearch(child, normalizedQuery) ||
      subjectHasMatchingDescendant(child, buckets, normalizedQuery),
  );
}

export function getAutoExpandedIdsForMatches(
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
  normalizedQuery: string,
): Set<number> {
  const autoExpandedIds = new Set<number>();
  if (!normalizedQuery) return autoExpandedIds;

  const visit = (subject: SubjectItem): boolean => {
    const childHasMatch = (buckets[String(subject.id)] || []).some((child) =>
      visit(child),
    );
    const selfMatches = subjectMatchesSearch(subject, normalizedQuery);
    if (childHasMatch) {
      autoExpandedIds.add(subject.id);
    }
    return selfMatches || childHasMatch;
  };

  rootSubjects.forEach((subject) => visit(subject));
  return autoExpandedIds;
}

export function getVisibleSubjectTree(
  rootSubjects: SubjectItem[],
  buckets: Record<string, SubjectItem[]>,
  expandedIds: Set<number>,
  pickerMode: "tree" | "list",
  normalizedQuery: string,
  searchCollapsedIds: Set<number>,
): SubjectTreeView {
  if (!normalizedQuery) {
    return {
      rows: getVisibleSubjectRows(rootSubjects, buckets, expandedIds, pickerMode),
      matchingIds: new Set<number>(),
      matchCount: 0,
      autoExpandedIds: new Set<number>(),
      effectiveExpandedIds: expandedIds,
      firstMatchId: null,
    };
  }

  const matchingIds = new Set<number>();
  const rows: VisibleSubjectRow[] = [];
  const autoExpandedIds = getAutoExpandedIdsForMatches(
    rootSubjects,
    buckets,
    normalizedQuery,
  );
  const effectiveExpandedIds = new Set<number>([
    ...Array.from(expandedIds),
    ...Array.from(autoExpandedIds),
  ]);

  searchCollapsedIds.forEach((subjectId) => effectiveExpandedIds.delete(subjectId));

  const collectMatches = (subject: SubjectItem) => {
    if (subjectMatchesSearch(subject, normalizedQuery)) {
      matchingIds.add(subject.id);
    }
    (buckets[String(subject.id)] || []).forEach((child) =>
      collectMatches(child),
    );
  };

  rootSubjects.forEach((subject) => collectMatches(subject));

  const visit = (subject: SubjectItem, depth: number) => {
    const selfMatches = matchingIds.has(subject.id);
    const descendantMatches = subjectHasMatchingDescendant(
      subject,
      buckets,
      normalizedQuery,
    );

    if (!selfMatches && !descendantMatches) return;

    rows.push({ subject, depth });

    if (!effectiveExpandedIds.has(subject.id)) return;

    (buckets[String(subject.id)] || []).forEach((child) =>
      visit(child, depth + 1),
    );
  };

  rootSubjects.forEach((subject) => visit(subject, 0));

  return {
    rows,
    matchingIds,
    matchCount: matchingIds.size,
    autoExpandedIds,
    effectiveExpandedIds,
    firstMatchId:
      rows.find((row) => matchingIds.has(row.subject.id))?.subject.id || null,
  };
}

export function buildGistTree(gists: GistItem[]): GistTreeNode[] {
  const byId = new Map<number, GistTreeNode>();
  const ids = new Set(gists.map((gist) => gist.id));

  gists
    .slice()
    .sort((a, b) => a.title.localeCompare(b.title))
    .forEach((gist) => {
      byId.set(gist.id, { gist, children: [] });
    });

  const roots: GistTreeNode[] = [];

  byId.forEach((node) => {
    const parentId = node.gist.parentId;
    if (typeof parentId === "number" && ids.has(parentId)) {
      byId.get(parentId)?.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: GistTreeNode[]) => {
    nodes.sort((a, b) =>
      compareGistsCanonically(a.gist, b.gist, (fallbackA, fallbackB) => {
        const aDate =
          countGistMetrics(fallbackA).latestActivity || fallbackA.createdAt || "";
        const bDate =
          countGistMetrics(fallbackB).latestActivity || fallbackB.createdAt || "";
        if (a.children.length !== b.children.length)
          return b.children.length - a.children.length;
        const dateDiff = bDate.localeCompare(aDate);
        return dateDiff || fallbackA.title.localeCompare(fallbackB.title);
      }),
    );
    nodes.forEach((node) => sortNodes(node.children));
  };

  sortNodes(roots);
  return roots;
}

export function flattenGistTree(nodes: GistTreeNode[]): GistItem[] {
  const result: GistItem[] = [];
  const visit = (node: GistTreeNode) => {
    result.push(node.gist);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
}


export function buildLegacySingleGistWorkspace(gist: GistItem): GistType[] {
  return groupGistsByParent([{ ...gist, parentId: null }] as unknown as GistType[]);
}

export function getGistDepth(gistId: number, gists: GistItem[]): number {
  const byId = new Map(gists.map((gist) => [gist.id, gist]));
  const visited = new Set<number>();
  let depth = 0;
  let current = byId.get(gistId);

  while (
    current &&
    typeof current.parentId === "number" &&
    byId.has(current.parentId) &&
    depth < 8
  ) {
    if (visited.has(current.id)) break;
    visited.add(current.id);
    depth += 1;
    current = byId.get(current.parentId);
  }

  return depth;
}
